import { SEGMENTS, TOTAL_MILES, type Segment } from "./segments.ts";
import { MILESTONES, type Milestone } from "./milestones.ts";
import { PATH, type PathNode } from "./path.ts";

export const METERS_PER_MILE = 1609.344;
export const milesFromMeters = (m: number) => m / METERS_PER_MILE;
export const metersFromMiles = (mi: number) => mi * METERS_PER_MILE;

export interface Position {
  x: number;
  y: number;
}

export interface Progress {
  totalMiles: number;
  /** 0..1 across the entire 3,871-mile journey. */
  journeyFraction: number;
  /** 0..1 across the 1,779-mile quest to Mount Doom. */
  questFraction: number;
  segment: Segment;
  milesIntoSegment: number;
  segmentFraction: number;
  milesToSegmentEnd: number;
  lastMilestone: Milestone | null;
  nextMilestone: Milestone | null;
  milesToNextMilestone: number | null;
  position: Position;
  finished: boolean;
}

export function segmentAt(mile: number): Segment {
  const m = clamp(mile, 0, TOTAL_MILES);
  // At an exact boundary the walker has *arrived*; show the leg they completed
  // until they log more miles (except at mile 0).
  for (const s of SEGMENTS) {
    if (m > s.startMile && m <= s.endMile) return s;
  }
  return SEGMENTS[0];
}

export function milestonesReached(mile: number): Milestone[] {
  return MILESTONES.filter((ms) => ms.mile <= mile);
}

export function nextMilestoneAfter(mile: number): Milestone | null {
  return MILESTONES.find((ms) => ms.mile > mile) ?? null;
}

/** Milestones passed when moving from `fromMile` to `toMile` (exclusive/inclusive). */
export function milestonesCrossed(fromMile: number, toMile: number): Milestone[] {
  if (toMile <= fromMile) return [];
  return MILESTONES.filter((ms) => ms.mile > fromMile && ms.mile <= toMile);
}

/** Interpolate a map position for a cumulative mile along the anchor path. */
export function positionAt(mile: number, path: PathNode[] = PATH): Position {
  const anchors = path.filter((p): p is PathNode & { mile: number } => p.mile !== null);
  if (anchors.length === 0) return { x: 0, y: 0 };
  const m = clamp(mile, anchors[0].mile, anchors[anchors.length - 1].mile);

  for (let i = 0; i < anchors.length - 1; i++) {
    const a = anchors[i];
    const b = anchors[i + 1];
    if (m < a.mile || m > b.mile) continue;
    // Walk the polyline between these two anchors (including geometry nodes).
    const ai = path.indexOf(a);
    const bi = path.indexOf(b);
    const poly = path.slice(ai, bi + 1);
    const total = polylineLength(poly);
    const span = b.mile - a.mile;
    const t = span === 0 ? 1 : (m - a.mile) / span;
    return pointAlong(poly, total * t);
  }
  const last = anchors[anchors.length - 1];
  return { x: last.x, y: last.y };
}

export function progressFor(totalMiles: number): Progress {
  const miles = Math.max(0, totalMiles);
  const clamped = Math.min(miles, TOTAL_MILES);
  const segment = segmentAt(clamped);
  const milesIntoSegment = clamped - segment.startMile;
  const reached = milestonesReached(clamped);
  const next = nextMilestoneAfter(clamped);
  const questMiles = SEGMENTS[3].endMile;
  return {
    totalMiles: miles,
    journeyFraction: clamped / TOTAL_MILES,
    questFraction: Math.min(clamped, questMiles) / questMiles,
    segment,
    milesIntoSegment,
    segmentFraction: milesIntoSegment / segment.miles,
    milesToSegmentEnd: segment.endMile - clamped,
    lastMilestone: reached.length ? reached[reached.length - 1] : null,
    nextMilestone: next,
    milesToNextMilestone: next ? next.mile - clamped : null,
    position: positionAt(clamped),
    finished: miles >= TOTAL_MILES,
  };
}

// --- helpers ---------------------------------------------------------------

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

function dist(a: Position, b: Position) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function polylineLength(pts: Position[]) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += dist(pts[i - 1], pts[i]);
  return len;
}

function pointAlong(pts: Position[], d: number): Position {
  if (pts.length === 1) return { x: pts[0].x, y: pts[0].y };
  let remaining = d;
  for (let i = 1; i < pts.length; i++) {
    const seg = dist(pts[i - 1], pts[i]);
    if (remaining <= seg || i === pts.length - 1) {
      const t = seg === 0 ? 0 : clamp(remaining / seg, 0, 1);
      return {
        x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t,
        y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t,
      };
    }
    remaining -= seg;
  }
  const last = pts[pts.length - 1];
  return { x: last.x, y: last.y };
}
